import {gql} from '@apollo/client';
import {Group, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_SEMIDARK, NEAR_BLACK, SILVER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {putJSONPromise, isServerRequestError} from 'util/Net';

import type {
  UserToolPermissionsModalQuery,
  UserToolPermissionsModalQueryVariables,
} from './__generated/UserToolPermissionsModalQuery';
import McpAuthPrompt from './McpAuthPrompt';
import {getPermissionLabel, McpToolPermissionValue, sharedStyles} from './McpModalStyles';
import McpToolList, {ToolInfo} from './McpToolList';
import {useMCPPing} from './useMCPPing';

export const USER_TOOL_PERMISSIONS_QUERY = gql`
  query UserToolPermissionsModalQuery($userConfigId: StrictString!) @skipAudit @engComponent(name: LLM) {
    mcpServerUserConfig(id: $userConfigId) {
      id
      mcpServerConfig {
        id
        name
        serverUrl
        toolPermissions {
          toolName
          permission
        }
        tools {
          name
          description
          parameters
        }
      }
      toolPermissions {
        toolName
        permission
      }
    }
  }
`;

interface ToolPermission {
  toolName: string;
  permission: McpToolPermissionValue;
}

interface UserToolPermissionsModalProps {
  userConfigId: string;
  serverId: string;
  serverName: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

interface EffectivePermission {
  permission: McpToolPermissionValue;
  isEditable: boolean;
}

function getEffectiveUserPermission(
  toolName: string,
  adminPermissions: Array<ToolPermission>,
  userPermissions: Map<string, McpToolPermissionValue | null>,
  originalUserPermissions: Array<ToolPermission>
): EffectivePermission {
  const adminPerm = adminPermissions.find((p) => p.toolName === toolName);

  // Admin must have AUTO for user to edit
  if (adminPerm?.permission !== 'AUTO') {
    return {permission: 'DISABLED', isEditable: false};
  }

  // Check if user has a local change
  const userPerm = userPermissions.get(toolName);
  if (userPerm !== undefined) {
    return {permission: userPerm ?? 'AUTO', isEditable: true};
  }

  // Check original user permissions
  const originalPerm = originalUserPermissions.find((p) => p.toolName === toolName);
  if (originalPerm) {
    return {permission: originalPerm.permission, isEditable: true};
  }

  // User inherits from admin
  return {permission: 'AUTO', isEditable: true};
}

export default function UserToolPermissionsModal(props: UserToolPermissionsModalProps): JSX.Element {
  const {userConfigId, serverId, serverName, onRequestClose} = props;

  const [userPermissions, setUserPermissions] = useState<Map<string, McpToolPermissionValue | null>>(
    new Map()
  );
  const [permissionsInitialized, setPermissionsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedThisSession, setHasSavedThisSession] = useState(false);

  // Query for user config and permissions
  const userConfigQuery = useBenchlingQuery<
    UserToolPermissionsModalQuery,
    UserToolPermissionsModalQueryVariables
  >(USER_TOOL_PERMISSIONS_QUERY, {
    variables: {userConfigId},
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (!permissionsInitialized && data.mcpServerUserConfig?.toolPermissions) {
        const permMap = new Map<string, McpToolPermissionValue | null>();
        for (const perm of data.mcpServerUserConfig.toolPermissions) {
          permMap.set(perm.toolName, perm.permission as McpToolPermissionValue);
        }
        setUserPermissions(permMap);
        setPermissionsInitialized(true);
      }
    },
  });
  const pingResult = useMCPPing({
    serverId,
    enabled: true,
  });

  const adminPermissions: Array<ToolPermission> =
    userConfigQuery.type === QueryResultType.SUCCESS
      ? ((userConfigQuery.data.mcpServerUserConfig?.mcpServerConfig
          .toolPermissions as Array<ToolPermission>) ?? [])
      : [];

  // Derive state from query result
  const isLoading = userConfigQuery.type === QueryResultType.PENDING;
  const hasError = userConfigQuery.type === QueryResultType.ERROR;

  const configData =
    userConfigQuery.type === QueryResultType.SUCCESS ? userConfigQuery.data.mcpServerUserConfig : null;
  const tools: Array<ToolInfo> = configData?.mcpServerConfig?.tools ?? [];
  const originalUserPermissions: Array<ToolPermission> = useMemo(
    () => (configData?.toolPermissions as Array<ToolPermission>) ?? [],
    [configData?.toolPermissions]
  );

  const isDirty = useMemo(() => {
    if (!permissionsInitialized) {
      return false;
    }
    if (userPermissions.size !== originalUserPermissions.length) {
      return true;
    }
    for (const orig of originalUserPermissions) {
      if (userPermissions.get(orig.toolName) !== orig.permission) {
        return true;
      }
    }
    return false;
  }, [userPermissions, originalUserPermissions, permissionsInitialized]);

  const needsAuth = pingResult.status?.needsRedirect ?? false;
  const authRedirectUrl = pingResult.status?.redirectUrl ?? null;

  const handlePermissionChange = (toolName: string, value: McpToolPermissionValue): void => {
    setUserPermissions((prev) => {
      const next = new Map(prev);
      next.set(toolName, value);
      return next;
    });
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      const toolPermissions = Array.from(userPermissions.entries())
        .filter(([, permission]) => permission != null)
        .map(([toolName, permission]) => ({toolName, permission}));

      await putJSONPromise({
        url: `/1/api/llm/mcp-server-user-configs/${userConfigId}`,
        data: {toolPermissions},
      });

      notifications.success('Tool preferences saved successfully');
      setHasSavedThisSession(true);
    } catch (e) {
      if (isServerRequestError(e)) {
        notifications.error('Failed to save tool preferences');
      } else {
        throw e;
      }
    } finally {
      setIsSaving(false);
    }
  };

  const renderAccessControl = (tool: ToolInfo): JSX.Element => {
    const effectivePerm = getEffectiveUserPermission(
      tool.name,
      adminPermissions,
      userPermissions,
      originalUserPermissions
    );

    if (!effectivePerm.isEditable) {
      return (
        <Group spacing={4} noWrap={true} className={css(styles.lockedIndicator)}>
          <BenchlingIcon icon="lock" className={css(styles.lockIcon)} />
          <Text className={css(styles.lockedLabel)}>Disabled by Admin</Text>
        </Group>
      );
    }

    return (
      <SelectDropdown
        displayName={getPermissionLabel(effectivePerm.permission)}
        menu={
          <Menu value={effectivePerm.permission}>
            <Option
              value="AUTO"
              onSelect={() => {
                handlePermissionChange(tool.name, 'AUTO');
              }}
            >
              Granted
            </Option>
            <Option
              value="DISABLED"
              onSelect={() => {
                handlePermissionChange(tool.name, 'DISABLED');
              }}
            >
              Not Granted
            </Option>
          </Menu>
        }
      />
    );
  };

  return (
    <Modal
      title={`Manage Tools - ${serverName}`}
      closeableOnBackgroundClick={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSave}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: isSaving ? 'Saving...' : 'Save',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: isLoading || hasError || isSaving || (hasSavedThisSession && !isDirty),
        },
      ]}
    >
      <div className={css(styles.modalContent)}>
        <div className={css(styles.toolPermissionsHeader)}>
          <Text className={css(styles.sectionTitle)}>Tool permissions</Text>
          <Text className={css(styles.sectionDescription)}>
            Manage which tools are available for your use.
          </Text>
        </div>

        <div className={css(styles.toolListContainer)}>
          {isLoading ? (
            <div className={css(sharedStyles.loadingState)}>
              <BenchlingIcon icon="refresh" className={css(sharedStyles.spinningIcon)} />
              <Text>Loading tools...</Text>
            </div>
          ) : needsAuth && authRedirectUrl ? (
            <McpAuthPrompt authRedirectUrl={authRedirectUrl} onAuthComplete={pingResult.refetch} />
          ) : (
            <McpToolList
              tools={tools}
              error={hasError ? 'Failed to load tools.' : null}
              renderAccessControl={renderAccessControl}
              expandable={true}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    minHeight: 300,
    maxHeight: 500,
    display: 'flex',
    flexDirection: 'column',
  },
  toolPermissionsHeader: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: NEAR_BLACK,
  },
  sectionDescription: {
    fontSize: 13,
    color: GRAY_SEMIDARK,
    marginTop: 4,
  },
  toolListContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  lockedIndicator: {
    padding: '4px 8px',
  },
  lockIcon: {
    color: SILVER,
    fontSize: 12,
  },
  lockedLabel: {
    fontSize: 12,
    color: SILVER,
    fontWeight: 500,
  },
});
